import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Form, Input, Modal, Pagination, Typography, message,
} from 'antd';
import { ThemeContext } from '../../../context/ThemeContext';
import LandingLayout from '../LandingLayout';
import apiClient from '../../../services/apiClient';
import { STRATEGIES, getStrategyCategoryInfo, getStrategySelectableCategories, getStrategyDefaultCategoryKey } from './strategyData';
import MyPortfolioHoldingsTable from './MyPortfolioHoldingsTable';
import StrategyCategorySelector from './StrategyCategorySelector';
import PortfolioBondCardList from './PortfolioBondCardList';
import PortfolioRefineFilters from './PortfolioRefineFilters';
import PortfolioStickyOverview from './PortfolioStickyOverview';
import {
  isGovtSecurity,
  getGovtIssuerDisplayName,
  getCouponValueFromNomenclature,
} from './portfolioBondDisplay';
import './HighReturnPage.css';
import './PortfolioBondCardList.css';
import './PortfolioRefineFilters.css';
import './PortfolioStickyOverview.css';
import '../LandingHero.css';
import './PortfolioHeader.css';
import './PortfolioTables.css';
import '../InstrumentDetails.css';
import { usePortfolioUser } from './usePortfolioUser';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import {
  mapCategoryForFilterApi,
  categoriesIncludeCentralGov,
  categoriesIncludeStateGov,
} from '../../../utils/categoryApiUtils';

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;

  const cleaned = String(v).replace('%', '').trim(); // remove %
  const n = Number(cleaned);

  return Number.isFinite(n) ? n : null;
};

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const str = String(value).trim();
  if (!str) return null;

  // Support dd-mm-yyyy and dd/mm/yyyy explicitly before Date fallback
  const m = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    const y = Number(m[3]);
    const dt = new Date(y, mo - 1, d);
    if (
      dt.getFullYear() === y &&
      dt.getMonth() === mo - 1 &&
      dt.getDate() === d
    ) {
      return dt;
    }
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
};

// Approximate yield (%) using:
// YTM ≈ (C + (F - P)/n) / ((F + P)/2), fallback to current yield (C/P)
const calcApproxYieldPercent = ({
  couponRatePct,
  faceValue,
  price,
  maturityDate
}) => {
  const F = toNumberOrNull(faceValue) ?? 100;
  const P = toNumberOrNull(price);
  const cPct = toNumberOrNull(couponRatePct);
  if (P == null || cPct == null || P <= 0) return null;

  // If bought at face value, yield should be very close to coupon rate.
  if (Math.abs(P - F) < 1e-9) return Number(cPct.toFixed(2));

  const C = (cPct / 100) * F; // annual coupon amount
  const maturity = parseFlexibleDate(maturityDate);
  const now = new Date();
  if (maturity) {
    const ms = maturity.getTime() - now.getTime();
    const n = ms / (1000 * 60 * 60 * 24 * 365.25);
    if (Number.isFinite(n) && n > 0) {
      const ytm = (C + ((F - P) / n)) / ((F + P) / 2);
      if (Number.isFinite(ytm)) return Number((ytm * 100).toFixed(2));
    }
  }

  // Fallback when maturity is missing/invalid.
  const currentYield = C / P;
  return Number.isFinite(currentYield) ? Number((currentYield * 100).toFixed(2)) : null;
};

const normalizeRatingKey = (value) => {
  if (!value) return null;
  // If backend gives rating list objects like [{currentRating:"AAA"}, ...], prefer first non-empty currentRating.
  if (Array.isArray(value)) {
    for (const item of value) {
      if (!item) continue;
      if (typeof item === 'string') {
        const s = item.trim();
        if (s) return s;
      } else if (typeof item === 'object') {
        const s = String(item.currentRating || item.rating || '').trim();
        if (s) return s;
      }
    }
    return null;
  }
  const str = String(value).trim();
  if (!str) return null;
  // handle "AA+,AA" etc.
  const first = str.split(/[,|/]/)[0];
  return first ? first.trim() : str;
};

const couponFrequencyLabel = (record) => {
  const freq = record?.couponPaymentFrequency || record?.interestPaymentFrequency;
  return freq || null;
};

function mergeCategoryResults(resultA, resultB) {
  let combined = [];
  let total = 0;

  if (resultA?.statusCode === 200 && resultA?.data) {
    combined = [...(resultA.data.content || [])];
    total += (resultA.data.totalElements || 0);
  }

  if (resultB?.statusCode === 200 && resultB?.data) {
    const content = resultB.data.content || [];
    const existingIsins = new Set(combined.map((b) => b.isin));
    content.forEach((bond) => {
      if (!existingIsins.has(bond.isin)) {
        combined.push(bond);
      } else {
        total--;
      }
    });
    total += (resultB.data.totalElements || 0);
  }

  return { combined, total };
}

export default function PortfolioUniversePage({
  strategyId,
  portfolioHoldingType,
  skipLayout = false,
}) {
  const strategy = useMemo(() => STRATEGIES.find((s) => s.id === strategyId), [strategyId]);
  const selectableCategories = useMemo(
    () => getStrategySelectableCategories(strategyId),
    [strategyId],
  );
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(
    () => getStrategyDefaultCategoryKey(strategyId),
  );
  const categories = useMemo(() => {
    if (selectableCategories.length === 0) return [];
    return selectedCategoryKey ? [selectedCategoryKey] : [];
  }, [selectableCategories.length, selectedCategoryKey]);
  const activeCategoryInfo = useMemo(
    () => getStrategyCategoryInfo(strategyId, categories),
    [strategyId, categories],
  );
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  const { userId, isAuthenticated, requireAuth, authHeaders } = usePortfolioUser();

  const isCentralGov = useMemo(() => categoriesIncludeCentralGov(categories), [categories]);
  const isStateGov = useMemo(() => categoriesIncludeStateGov(categories), [categories]);
  const showRatingFilter = !isCentralGov && !isStateGov;

  // Restore state from router if available
  const savedState = location.state || {};
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedState.pagination?.currentPage || 1);
  const [pageSize, setPageSize] = useState(savedState.pagination?.pageSize || 12);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(savedState.hasSearched || false);

  const [redemptionFilter, setRedemptionFilter] = useState(savedState.filters?.redemptionFilter || null);
  const [couponFilter, setCouponFilter] = useState(savedState.filters?.couponFilter || null);
  const [ratingFilter, setRatingFilter] = useState(savedState.filters?.ratingFilter || null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const activeCount = [
    redemptionFilter,
    couponFilter,
    showRatingFilter && ratingFilter,
  ].filter(Boolean).length;

  // Add-to-portfolio modal state (same behavior as InstrumentDetails)
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [marketPriceLoading, setMarketPriceLoading] = useState(false);
  const [selectedBond, setSelectedBond] = useState(null);
  const [portfolioForm] = Form.useForm();
  /** Shown as plain text only (not a form textbox). */
  const [portfolioModalMarketPriceByScan, setPortfolioModalMarketPriceByScan] = useState(null);
  const [portfolioModalYieldByScan, setPortfolioModalYieldByScan] = useState(null);
  const [portfolioModalAvgScan, setPortfolioModalAvgScan] = useState(null);
  const [portfolioModalScanScore, setPortfolioModalScanScore] = useState(null);
  const [holdingsRefreshSignal, setHoldingsRefreshSignal] = useState(0);
  const [strategyHoldings, setStrategyHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [categoryIsins, setCategoryIsins] = useState(() => new Set());
  const trackedCategoryKeyRef = useRef(selectedCategoryKey);
  const holdingsSectionRef = useRef(null);

  const categoryHoldings = useMemo(() => {
    if (categories.length === 0 || categoryIsins.size === 0) {
      return strategyHoldings;
    }
    return strategyHoldings.filter((h) => h?.isin && categoryIsins.has(h.isin));
  }, [strategyHoldings, categoryIsins, categories.length]);

  const fetchStrategyHoldings = useCallback(async () => {
    if (!portfolioHoldingType || !userId) {
      setStrategyHoldings([]);
      return;
    }
    try {
      setHoldingsLoading(true);
      const res = await apiClient.get(`${baseUrl}/user/portfolio/list/${userId}`, {
        params: { type: portfolioHoldingType },
        headers: authHeaders(),
      });
      setStrategyHoldings(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load strategy holdings', err);
      setStrategyHoldings([]);
    } finally {
      setHoldingsLoading(false);
    }
  }, [baseUrl, portfolioHoldingType, userId, authHeaders]);

  const fetchCategoryIsins = useCallback(async () => {
    const cat = selectedCategoryKey;
    if (!cat) {
      setCategoryIsins(new Set());
      return;
    }
    try {
      const res = await apiClient.get(
        `${baseUrl}/isin-category/category/${cat}?page=0&size=1000`,
      );
      const content = res.data?.data?.content || [];
      setCategoryIsins(new Set(content.map((row) => row.isin).filter(Boolean)));
    } catch (err) {
      console.error('Failed to load category ISINs for overview', err);
      setCategoryIsins(new Set());
    }
  }, [baseUrl, selectedCategoryKey]);

  useEffect(() => {
    fetchStrategyHoldings();
  }, [fetchStrategyHoldings, holdingsRefreshSignal]);

  useEffect(() => {
    fetchCategoryIsins();
  }, [fetchCategoryIsins, holdingsRefreshSignal]);

  const scrollToHoldings = useCallback(() => {
    holdingsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCategoryChange = useCallback((categoryKey) => {
    setSelectedCategoryKey(categoryKey);
    setCurrentPage(1);
    setHasSearched(false);
    setFiltersExpanded(false);
    setRedemptionFilter(null);
    setCouponFilter(null);
    setRatingFilter(null);
  }, []);

  // Fetch all data without any filters (used on initial load and after clear)
  const fetchAllData = useCallback(async (page = 0, size = pageSize) => {
    try {
      setLoading(true);
      const cats = Array.isArray(categories) ? categories : [];

      if (cats.length === 0) {
        // No specific categories configured — fall back to the unfiltered master endpoint
        const res = await apiClient.get(`${baseUrl}/master-isin/all?page=${page}&size=${size}`);
        const result = res.data;
        if (result?.statusCode === 200 && result?.data) {
          setRows(result.data.content || []);
          setTotalItems(result.data.totalElements || 0);
          setCurrentPage(page + 1);
        } else {
          setRows([]);
          setTotalItems(0);
        }
        return;
      }

      // Fetch each category in parallel using GET /isin-category/category/{cat}
      const requests = cats.map((cat) =>
        apiClient
          .get(`${baseUrl}/isin-category/category/${cat}?page=${page}&size=${size}`)
          .catch(() => ({ data: null }))
      );
      const responses = await Promise.all(requests);
      const results = responses.map((r) => r?.data);

      let combined = [];
      let total = 0;
      for (const r of results) {
        const merged = mergeCategoryResults(
          { statusCode: 200, data: { content: combined, totalElements: total } },
          r
        );
        combined = merged.combined;
        total = merged.total;
      }

      setRows(combined);
      setTotalItems(total);
      setCurrentPage(page + 1);
    } catch (err) {
      console.error('Failed to load data:', err);
      message.error('Failed to load data.');
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, pageSize, categories]);

  const applyFilters = useCallback(async (page = 0, size = pageSize, filterOverrides = null) => {
    try {
      setFilterLoading(true);
      setLoading(true);

      const redemption = filterOverrides?.redemption !== undefined
        ? filterOverrides.redemption
        : redemptionFilter;
      const coupon = filterOverrides?.coupon !== undefined
        ? filterOverrides.coupon
        : couponFilter;
      const rating = filterOverrides?.rating !== undefined
        ? filterOverrides.rating
        : ratingFilter;

      const payload = {};
      if (redemption) payload.redemption = redemption;
      if (coupon) payload.couponRate = coupon;
      if (showRatingFilter && rating) payload.ratings = rating;

      const cats = Array.isArray(categories) ? categories : [];
      if (cats.length === 0) {
        const res = await apiClient.post(`${baseUrl}/master-isin/filter?page=${page}&size=${size}`, payload);
        const result = res.data;
        if (result?.statusCode === 200 && result?.data) {
          setRows(result.data.content || []);
          setTotalItems(result.data.totalElements || 0);
          setCurrentPage(page + 1);
          setHasSearched(true);
        } else {
          setRows([]);
          setTotalItems(0);
          setCurrentPage(page + 1);
          setHasSearched(true);
        }
        return;
      }

      const requests = cats.map((cat) =>
        apiClient.post(
          `${baseUrl}/master-isin/filter?page=${page}&size=${size}&category=${mapCategoryForFilterApi(cat)}`,
          payload
        ).catch(() => ({ data: null }))
      );

      const responses = await Promise.all(requests);
      const results = responses.map((r) => r?.data);

      let combined = [];
      let total = 0;
      for (const r of results) {
        const merged = mergeCategoryResults({ statusCode: 200, data: { content: combined, totalElements: total } }, r);
        combined = merged.combined;
        total = merged.total;
      }

      if (combined.length > 0) {
        setRows(combined);
        setTotalItems(total);
        setCurrentPage(page + 1);
        setHasSearched(true);
      } else {
        setRows([]);
        setTotalItems(0);
        setCurrentPage(page + 1);
        setHasSearched(true);
        message.info('No results found for the selected filters.');
      }
    } catch (err) {
      console.error('Filter error:', err);
      message.error('Failed to apply filters.');
      setRows([]);
      setTotalItems(0);
      setHasSearched(true);
    } finally {
      setFilterLoading(false);
      setLoading(false);
    }
  }, [baseUrl, pageSize, redemptionFilter, couponFilter, ratingFilter, categories, showRatingFilter]);

  // Sync state to location so "Back" button finds it
  useEffect(() => {
    navigate(location.pathname, {
      replace: true,
      state: {
        filters: { redemptionFilter, couponFilter, ratingFilter },
        pagination: { currentPage, pageSize },
        hasSearched
      }
    });
  }, [navigate, location.pathname, redemptionFilter, couponFilter, ratingFilter, currentPage, pageSize, hasSearched]);

  // Load data on mount and whenever the user picks a different ISIN category
  useEffect(() => {
    const categoryChanged = trackedCategoryKeyRef.current !== selectedCategoryKey;
    trackedCategoryKeyRef.current = selectedCategoryKey;

    if (!categoryChanged && savedState.hasSearched) {
      applyFilters(savedState.pagination.currentPage - 1, savedState.pagination.pageSize);
      return;
    }
    fetchAllData(0, pageSize);
  }, [selectedCategoryKey, strategyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortfolioModal = useCallback((bond) => {
    if (!bond?.isin) {
      message.error('ISIN not available');
      return;
    }
    const auth = requireAuth('Please log in to add bonds to your portfolio');
    if (!auth.ok) return;
    setSelectedBond(bond);
    setPortfolioModalMarketPriceByScan(null);
    setPortfolioModalYieldByScan(null);
    setPortfolioModalAvgScan(null);
    setPortfolioModalScanScore(null);
    setPortfolioModalOpen(true);
    portfolioForm.setFieldsValue({ quantity: 1, marketPrice: undefined, ytm: undefined });
  }, [portfolioForm, requireAuth]);

  // ── Government security helpers imported from portfolioBondDisplay ───────

  const handleViewBond = useCallback((bond) => {
    if (!bond?.isin || isGovtSecurity(bond)) return;
    navigate(`/isin-details/${bond.isin}`, {
      state: {
        filters: { redemptionFilter, couponFilter, ratingFilter },
        pagination: { currentPage, pageSize },
        hasSearched,
      },
    });
  }, [navigate, redemptionFilter, couponFilter, ratingFilter, currentPage, pageSize, hasSearched]);

  const handleSaveToPortfolio = useCallback(async () => {
    if (!selectedBond?.isin) return;
    const auth = requireAuth('Please log in to add bonds to your portfolio');
    if (!auth.ok) return;
    try {
      const values = await portfolioForm.validateFields();
      const govtBond = isGovtSecurity(selectedBond);

      // Explicitly convert to numbers
      const qty = Number(values.quantity);

      // G-Sec logic vs Corporate
      const maturityDate = govtBond
        ? (selectedBond.dateOfMaturity || selectedBond.redemption || undefined)
        : (selectedBond.redemption || selectedBond.dateOfRedemption || undefined);

      const rawCoupon = govtBond
        ? (getCouponValueFromNomenclature(selectedBond) || selectedBond.couponRate || selectedBond.coupon)
        : (selectedBond.couponRate || selectedBond.coupon);

      const couponRatePct = toNumberOrNull(rawCoupon);

      // For facevalue use outstandingStock for Govt bonds
      const faceValue = govtBond
        ? (selectedBond.outstandingStock || 100)
        : (selectedBond.faceValue || selectedBond.facevalue || selectedBond.face_value || 100);

      // Use computed market price (disabled field). Fallback to face value if unavailable.
      const price = Number(toNumberOrNull(values.marketPrice) ?? toNumberOrNull(faceValue) ?? 100);

      // Use YTM if available, otherwise fallback to local approx yield
      const ytmPct = toNumberOrNull(values.ytm);
      const computedYield = ytmPct ?? calcApproxYieldPercent({
          couponRatePct,
          faceValue,
          price,
          maturityDate,
        });

      const payload = {
        quantity: qty,
        price: price,
        type: portfolioHoldingType,
        issuer: govtBond ? getGovtIssuerDisplayName(selectedBond) : (selectedBond.companyIssuerName || selectedBond.issuerName || undefined),
        issueDate: govtBond ? (selectedBond.dateOfIssue || selectedBond.allotmentDate || undefined) : (selectedBond.allotmentDate || selectedBond.issueDate || undefined),
        maturityDate,
        coupon: rawCoupon ?? undefined,
        yield: computedYield ?? selectedBond.yield ?? undefined,
      };

      setPortfolioLoading(true);
      await apiClient.post(`${baseUrl}/user/portfolio/${selectedBond.isin}`, payload, {
        headers: authHeaders(),
      });
      message.success('Added to portfolio');
      setPortfolioModalOpen(false);
      setHoldingsRefreshSignal((n) => n + 1);
    } catch (err) {
      if (err?.errorFields) return;
      console.error('Error saving portfolio holding:', err);
      message.error(getApiErrorMessage(err, 'Failed to save portfolio holding'));
    } finally {
      setPortfolioLoading(false);
    }
  }, [selectedBond, portfolioForm, baseUrl, portfolioHoldingType, requireAuth, authHeaders]);

  useEffect(() => {
    const run = async () => {
      if (!portfolioModalOpen || !selectedBond?.isin) return;

      const govtBond = isGovtSecurity(selectedBond);
      const maturityDate = govtBond
        ? (selectedBond.dateOfMaturity || selectedBond.redemption)
        : (selectedBond.redemption || selectedBond.dateOfRedemption);

      const maturity = parseFlexibleDate(maturityDate);
      if (!maturity) {
        setPortfolioModalMarketPriceByScan(null);
        setPortfolioModalYieldByScan(null);
        setPortfolioModalAvgScan(null);
        setPortfolioModalScanScore(null);
        portfolioForm.setFieldsValue({ marketPrice: undefined, ytm: undefined });
        return;
      }

      const now = new Date();
      const yearsToMaturity = (maturity.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (!Number.isFinite(yearsToMaturity) || yearsToMaturity <= 0) {
        setPortfolioModalMarketPriceByScan(null);
        setPortfolioModalYieldByScan(null);
        setPortfolioModalAvgScan(null);
        setPortfolioModalScanScore(null);
        portfolioForm.setFieldsValue({ marketPrice: undefined, ytm: undefined });
        return;
      }
      const maturityMonths = yearsToMaturity * 12;

      const rawCoupon = govtBond
        ? (getCouponValueFromNomenclature(selectedBond) || selectedBond.couponRate || selectedBond.coupon)
        : (selectedBond.couponRate || selectedBond.coupon);
      const couponRatePct = toNumberOrNull(rawCoupon);

      const faceValue = govtBond
        ? (selectedBond.outstandingStock || 100)
        : (selectedBond.faceValue || selectedBond.facevalue || selectedBond.face_value || 100);

      const ratingKey = normalizeRatingKey(selectedBond.creditRatings || selectedBond.creditRating || selectedBond.rating);
      const govtBenchmarkKey = govtBond
        ? (selectedBond.securityType === 'State Government Securities' ? 'SDL' : 'G-Sec')
        : null;
      const creditRatings =
        govtBenchmarkKey
          ? [{ currentRating: govtBenchmarkKey }]
          : (Array.isArray(selectedBond.creditRatings) && selectedBond.creditRatings.length > 0
          ? selectedBond.creditRatings
          : (ratingKey ? [{ currentRating: ratingKey }] : undefined));

      setMarketPriceLoading(true);
      try {
        const res = await apiClient.post(`${baseUrl}/yield/market-price`, {
          isin: selectedBond.isin,
          creditRatings,
          faceValue: toNumberOrNull(faceValue) ?? undefined,
          couponRatePct: couponRatePct ?? undefined,
          maturityDate: maturityDate || undefined,
          couponPaymentFrequency: couponFrequencyLabel(selectedBond) || undefined,
        });
        const ytmPct = res?.data?.ytmPercent ?? res?.data?.data?.ytmPercent;
        const marketPriceRaw = res?.data?.marketPrice ?? res?.data?.data?.marketPrice;
        const marketPriceByScanRaw = res?.data?.marketPriceByScan ?? res?.data?.data?.marketPriceByScan;
        const yieldByScanRaw = res?.data?.yieldByScan ?? res?.data?.data?.yieldByScan;
        const avgScanRaw = res?.data?.avgScan ?? res?.data?.data?.avgScan;
        const scanScoreRaw = res?.data?.scanScore ?? res?.data?.data?.scanScore;
        const ytm = toNumberOrNull(ytmPct);
        const marketPrice = toNumberOrNull(marketPriceRaw);
        setPortfolioModalMarketPriceByScan(toNumberOrNull(marketPriceByScanRaw));
        setPortfolioModalYieldByScan(toNumberOrNull(yieldByScanRaw));
        setPortfolioModalAvgScan(toNumberOrNull(avgScanRaw));
        setPortfolioModalScanScore(toNumberOrNull(scanScoreRaw));
        portfolioForm.setFieldsValue({
          ytm: ytm,
          marketPrice: marketPrice ?? undefined,
        });
      } catch (e) {
        console.error('Failed to resolve YTM / market price', e);
        setPortfolioModalMarketPriceByScan(null);
        setPortfolioModalYieldByScan(null);
        setPortfolioModalAvgScan(null);
        setPortfolioModalScanScore(null);
        portfolioForm.setFieldsValue({ marketPrice: undefined, ytm: undefined });
      } finally {
        setMarketPriceLoading(false);
      }
    };

    run();
  }, [portfolioModalOpen, selectedBond, baseUrl, portfolioForm]);

  const onClearAll = () => {
    setRedemptionFilter(null);
    setCouponFilter(null);
    setRatingFilter(null);
    setCurrentPage(1);
    setHasSearched(false);
    setFiltersExpanded(false);
    fetchAllData(0, pageSize);
  };

  const removeFilterChip = useCallback(async (key) => {
    const next = {
      redemption: key === 'redemption' ? null : redemptionFilter,
      coupon: key === 'coupon' ? null : couponFilter,
      rating: key === 'rating' ? null : ratingFilter,
    };

    setRedemptionFilter(next.redemption);
    setCouponFilter(next.coupon);
    setRatingFilter(next.rating);
    setCurrentPage(1);

    const hasAny = next.redemption || next.coupon || (showRatingFilter && next.rating);
    if (!hasAny) {
      setHasSearched(false);
      fetchAllData(0, pageSize);
      return;
    }
    await applyFilters(0, pageSize, next);
  }, [
    redemptionFilter, couponFilter, ratingFilter, showRatingFilter,
    fetchAllData, pageSize, applyFilters,
  ]);

  const content = (
    <div className={`hr-page${isDark ? ' dark' : ''}`}>
        <section className="hr-bonds-section">
          <button className="portfolio-back-link" onClick={() => navigate('/construct-portfolio')}>
            ← Back to Strategies
          </button>

          <header className="instruments-page-header">
            <h1 className="instruments-page-title">
              {strategy?.title}
            </h1>
            <p className="instruments-page-hint">{strategy?.tagline}</p>
          </header>

          <StrategyCategorySelector
            categories={selectableCategories}
            activeKey={selectedCategoryKey}
            onChange={handleCategoryChange}
            categoryInfo={selectableCategories.length < 2 ? activeCategoryInfo : null}
          />

          <div className="hr-explore-layout">
            <div className="hr-explore-main">
              <PortfolioRefineFilters
                expanded={filtersExpanded}
                onToggleExpanded={() => setFiltersExpanded((v) => !v)}
                redemptionFilter={redemptionFilter}
                onRedemptionChange={setRedemptionFilter}
                couponFilter={couponFilter}
                onCouponChange={setCouponFilter}
                ratingFilter={ratingFilter}
                onRatingChange={setRatingFilter}
                showRatingFilter={showRatingFilter}
                activeCount={activeCount}
                hasSearched={hasSearched}
                filterLoading={filterLoading}
                loading={loading}
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                onApply={() => applyFilters(0, pageSize)}
                onClearAll={onClearAll}
                onRemoveChip={removeFilterChip}
              />

          <div className="hr-bond-list-wrap">
            <PortfolioBondCardList
              rows={rows}
              loading={loading}
              hasSearched={hasSearched}
              onClearAll={onClearAll}
              onViewDetails={handleViewBond}
              onAddToPortfolio={openPortfolioModal}
            />
          </div>

          {totalItems > 0 && !loading && (
            <div className="hr-pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalItems}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                  if (hasSearched) applyFilters(page - 1, size);
                  else fetchAllData(page - 1, size);
                }}
                onShowSizeChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                  if (hasSearched) applyFilters(page - 1, size);
                  else fetchAllData(page - 1, size);
                }}
                showSizeChanger
                pageSizeOptions={['12', '24', '48', '96']}
                className="hr-ant-pagination"
              />
            </div>
          )}

            </div>

            {portfolioHoldingType && (
              <PortfolioStickyOverview
                strategy={strategy}
                categoryInfo={activeCategoryInfo}
                holdings={categoryHoldings}
                loading={holdingsLoading}
                isAuthenticated={isAuthenticated}
                onScrollToHoldings={scrollToHoldings}
              />
            )}
          </div>
        </section>

        {portfolioHoldingType && (
          <section className="hr-bonds-section" ref={holdingsSectionRef} id="portfolio-holdings-section">
            <MyPortfolioHoldingsTable
              type={portfolioHoldingType}
              refreshSignal={holdingsRefreshSignal}
              onRowsChanged={setStrategyHoldings}
            />
          </section>
        )}

        <Modal
          title="Add to Portfolio"
          open={portfolioModalOpen}
          onCancel={() => {
            setPortfolioModalOpen(false);
            setPortfolioModalMarketPriceByScan(null);
            setPortfolioModalYieldByScan(null);
            setPortfolioModalAvgScan(null);
            setPortfolioModalScanScore(null);
          }}
          onOk={handleSaveToPortfolio}
          okText="Save"
          confirmLoading={portfolioLoading}
          destroyOnClose
        >
          <Form form={portfolioForm} layout="vertical">
            <Form.Item label="YTM (%)" name="ytm">
              <Input disabled placeholder={marketPriceLoading ? 'Calculating…' : '—'} />
            </Form.Item>
            <Form.Item label="Market Price" name="marketPrice">
              <Input disabled placeholder={marketPriceLoading ? 'Calculating…' : '—'} />
            </Form.Item>
            {portfolioModalMarketPriceByScan != null && Number.isFinite(portfolioModalMarketPriceByScan) && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Market Price by Scan: {portfolioModalMarketPriceByScan}
              </Typography.Text>
            )}
            {portfolioModalYieldByScan != null && Number.isFinite(portfolioModalYieldByScan) && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Yield by Scan (%): {portfolioModalYieldByScan}
              </Typography.Text>
            )}
            {portfolioModalAvgScan != null && Number.isFinite(portfolioModalAvgScan) && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Avg Scan: {portfolioModalAvgScan}
              </Typography.Text>
            )}
            {portfolioModalScanScore != null && Number.isFinite(portfolioModalScanScore) && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Scan Score: {portfolioModalScanScore}
              </Typography.Text>
            )}
            <Form.Item
              label="Quantity"
              name="quantity"
              rules={[{ required: true, message: 'Please enter quantity' }]}
            >
              <Input type="number" placeholder="e.g. 10" />
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );

  return skipLayout ? content : (
    <LandingLayout>
      {content}
    </LandingLayout>
  );
}

